import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import client from "../api/client";
import { useMeta } from "../context/MetaContext";
import AiFieldActions from "../components/AiFieldActions";
import SimpleTextEditor from "../components/SimpleTextEditor";
import AiAssistOptionsModal from "../components/AiAssistOptionsModal";

function getEmptyAnswers(stage) {
  const questions = Array.isArray(stage?.questions) ? stage.questions : [];
  return questions.reduce((acc, _, index) => {
    acc[`q${index + 1}`] = "";
    return acc;
  }, {});
}

function parseJson(value) {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
}

function resolveAttachmentUrl(path) {
  if (!path) return "#";
  if (/^https?:\/\//i.test(path)) return path;
  const base = String(client.defaults.baseURL || "").replace(/\/api\/?$/, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

function cleanStageLabel(label = "") {
  return String(label).replace(/^[A-Z]\.\s*/, "").trim() || "Business Context";
}

export default function QuestionnairePage({ websiteId }) {
  const { assessmentTemplate, metaLoading, metaError } = useMeta();
  const location = useLocation();
  const navigate = useNavigate();
  const assessmentStages = assessmentTemplate?.stages || [];
  const openingGuidance = assessmentTemplate?.opening_guidance || [];
  const mainObjectives = assessmentTemplate?.main_objectives || [];

  const [selectedStage, setSelectedStage] = useState("");
  const [savedStages, setSavedStages] = useState([]);
  const [responses, setResponses] = useState({});
  const [incidents, setIncidents] = useState([]);
  const [answerIncidentMap, setAnswerIncidentMap] = useState({});
  const [attachmentsMap, setAttachmentsMap] = useState({});
  const [linkSavingKey, setLinkSavingKey] = useState("");
  const [uploadingKey, setUploadingKey] = useState("");
  const [activeIncidentPickerKey, setActiveIncidentPickerKey] = useState("");
  const [activeAttachmentPickerKey, setActiveAttachmentPickerKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiBusyKey, setAiBusyKey] = useState("");
  const [aiAssistMessage, setAiAssistMessage] = useState("");
  const [aiDialog, setAiDialog] = useState(null);
  const fileInputRefs = useRef({});

  const stage = useMemo(
    () => assessmentStages.find((entry) => entry.key === selectedStage) || assessmentStages[0] || { key: "", label: "", description: "", questions: [] },
    [assessmentStages, selectedStage]
  );

  useEffect(() => {
    if (!assessmentStages.length) return;
    if (!selectedStage || !assessmentStages.some((entry) => entry.key === selectedStage)) {
      setSelectedStage(assessmentStages[0].key);
    }
  }, [assessmentStages, selectedStage]);

  const load = async () => {
    if (!websiteId) return;
    const [qRes, iRes] = await Promise.all([
      client.get(`/websites/${websiteId}/questionnaires`),
      client.get(`/websites/${websiteId}/incidents`)
    ]);
    setSavedStages(qRes.data.data || []);
    setIncidents(iRes.data.data || []);
  };

  useEffect(() => {
    load();
  }, [websiteId]);

  useEffect(() => {
    if (!stage?.key) {
      setResponses({});
      return;
    }
    const stored = savedStages.find((row) => row.stage_key === selectedStage);
    const parsed = parseJson(stored?.responses_json);
    setResponses({ ...getEmptyAnswers(stage), ...parsed });
  }, [savedStages, selectedStage, stage]);

  const loadEvidenceForStage = async () => {
    if (!websiteId || !selectedStage) return;
    const { data } = await client.get(`/websites/${websiteId}/evidence`, { params: { stage_key: selectedStage } });

    const groupedLinks = {};
    (data.answer_incidents || []).forEach((row) => {
      const key = row.question_key;
      if (!groupedLinks[key]) groupedLinks[key] = [];
      groupedLinks[key].push(Number(row.incident_id));
    });
    setAnswerIncidentMap(groupedLinks);

    const groupedAttachments = {};
    (data.attachments || []).forEach((row) => {
      const key = row.question_key || "_general";
      if (!groupedAttachments[key]) groupedAttachments[key] = [];
      groupedAttachments[key].push(row);
    });
    setAttachmentsMap(groupedAttachments);
  };

  useEffect(() => {
    loadEvidenceForStage();
    setActiveIncidentPickerKey("");
    setActiveAttachmentPickerKey("");
  }, [websiteId, selectedStage]);

  useEffect(() => {
    const nextLabel = cleanStageLabel(stage.label);
    window.dispatchEvent(new CustomEvent("wpa:questionnaire-stage-change", { detail: { stageLabel: nextLabel } }));
  }, [stage]);

  const completion = useMemo(() => {
    const totalQuestions = Math.max(1, stage.questions.length);
    const filled = Object.values(responses).filter((item) => String(item || "").trim()).length;
    return Math.round((filled / totalQuestions) * 100);
  }, [responses, stage]);

  const saveStage = async () => {
    if (!websiteId) return;
    setSaving(true);
    try {
      await client.post(`/websites/${websiteId}/questionnaires`, {
        stage_key: stage.key,
        stage_label: stage.label,
        responses,
        completion_percent: completion
      });
      await load();
    } finally {
      setSaving(false);
    }
  };

  const runAiAssist = async ({ questionKey, mode, questionText, aiRules = null }) => {
    if (!websiteId) return;
    setAiBusyKey(`${questionKey}:${mode}`);
    setAiAssistMessage("");
    try {
      const { data } = await client.post("/ai/text-assist", {
        website_id: websiteId,
        mode,
        field_label: `${cleanStageLabel(stage.label)} - ${questionKey}`,
        current_text: responses[questionKey] || "",
        context: {
          page: "questionnaire",
          stage_key: stage.key,
          stage_label: cleanStageLabel(stage.label),
          question_key: questionKey,
          question_text: questionText,
          current_answer: responses[questionKey] || "",
          ai_rules: aiRules
        }
      }, {
        skipAuthRedirect: true
      });
      const nextText = data?.data?.text || "";
      setResponses((prev) => ({ ...prev, [questionKey]: nextText }));
    } catch (error) {
      setAiAssistMessage(error?.response?.data?.message || "AI assist could not generate content for this answer.");
    } finally {
      setAiBusyKey("");
    }
  };

  const generateCurrentStageWithAi = async (aiRules = null) => {
    if (!websiteId) return;
    setAiBusyKey("stage:generate");
    setAiAssistMessage("");
    try {
      const nextResponses = { ...responses };
      for (let index = 0; index < stage.questions.length; index += 1) {
        const questionText = stage.questions[index];
        const questionKey = `q${index + 1}`;
        const { data } = await client.post("/ai/text-assist", {
          website_id: websiteId,
          mode: "generate",
          field_label: `${cleanStageLabel(stage.label)} - ${questionKey}`,
          current_text: nextResponses[questionKey] || "",
          context: {
            page: "questionnaire",
            stage_key: stage.key,
            stage_label: cleanStageLabel(stage.label),
            question_key: questionKey,
            question_text: questionText,
            current_answer: nextResponses[questionKey] || "",
            ai_rules: aiRules
          }
        }, {
          skipAuthRedirect: true
        });
        const generatedText = data?.data?.text || "";
        if (generatedText) {
          nextResponses[questionKey] = generatedText;
        }
      }
      setResponses(nextResponses);
    } catch (error) {
      setAiAssistMessage(error?.response?.data?.message || "AI stage generation failed for this stage.");
    } finally {
      setAiBusyKey("");
    }
  };

  const saveLinkedIncidents = async (questionKey, incidentIds) => {
    if (!websiteId) return;
    setLinkSavingKey(questionKey);
    try {
      await client.post(`/websites/${websiteId}/evidence/answer-incidents`, {
        stage_key: stage.key,
        question_key: questionKey,
        incident_ids: incidentIds
      });
      setAnswerIncidentMap((prev) => ({ ...prev, [questionKey]: incidentIds }));
    } finally {
      setLinkSavingKey("");
    }
  };

  const onIncidentSelectionChange = (questionKey, values) => {
    setAnswerIncidentMap((prev) => ({ ...prev, [questionKey]: values }));
  };

  const onUploadAttachment = async (questionKey, file) => {
    if (!websiteId || !file) return;
    setUploadingKey(questionKey);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("stage_key", stage.key);
      formData.append("question_key", questionKey);
      await client.post(`/websites/${websiteId}/evidence/attachments`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      await loadEvidenceForStage();
    } finally {
      setUploadingKey("");
    }
  };

  const deleteAttachment = async (attachmentId) => {
    if (!websiteId || !attachmentId) return;
    await client.delete(`/websites/${websiteId}/evidence/attachments/${attachmentId}`);
    await loadEvidenceForStage();
  };

  const toggleIncidentPicker = (questionKey) => {
    setActiveAttachmentPickerKey("");
    setActiveIncidentPickerKey((prev) => (prev === questionKey ? "" : questionKey));
  };

  const toggleAttachmentPicker = (questionKey) => {
    setActiveIncidentPickerKey("");
    setActiveAttachmentPickerKey((prev) => (prev === questionKey ? "" : questionKey));
  };

  const openAiDialog = (payload) => {
    setAiDialog(payload);
  };

  const ensureAiReady = async () => {
    try {
      const { data } = await client.get("/ai/settings");
      const activeProvider = String(data?.preferences?.active_provider || "gemini");
      const provider = (data?.providers || []).find((item) => item.provider === activeProvider);
      const selectedModel = String(provider?.selected_model || "");
      const { data: testData } = await client.post("/ai/settings/test", {
        provider: activeProvider,
        model: selectedModel || undefined
      });
      if (testData?.ok) {
        return { ok: true };
      }
      return {
        ok: false,
        message: testData?.message || `${activeProvider} (${selectedModel || "default model"}) is not ready.`
      };
    } catch (error) {
      return {
        ok: false,
        message: error?.response?.data?.message || "AI provider is not configured. Open Settings and verify active provider model and API key."
      };
    }
  };

  const applyAiDialog = async (options) => {
    const rules = options?.applyRules
      ? {
          enabled: true,
          min_characters: Number(options.minCharacters || 0),
          min_headings: Number(options.minHeadings || 0),
          require_bullet_points: Boolean(options.requireBullets),
          extra_instruction: String(options.extraInstruction || "").trim()
        }
      : { enabled: false };

    const request = aiDialog;
    setAiDialog(null);
    if (!request) return;

    const readiness = await ensureAiReady();
    if (!readiness.ok) {
      setAiAssistMessage(readiness.message);
      return;
    }

    if (request.type === "stage") {
      await generateCurrentStageWithAi(rules);
      return;
    }

    await runAiAssist({
      questionKey: request.questionKey,
      mode: request.mode,
      questionText: request.questionText,
      aiRules: rules
    });
  };

  useEffect(() => {
    const onGenerateStage = () => {
      if (!aiBusyKey) {
        openAiDialog({ type: "stage", mode: "generate" });
      }
    };
    const onSaveStage = () => {
      if (!saving) {
        saveStage();
      }
    };
    window.addEventListener("wpa:questionnaire-generate-stage", onGenerateStage);
    window.addEventListener("wpa:questionnaire-save-stage", onSaveStage);
    return () => {
      window.removeEventListener("wpa:questionnaire-generate-stage", onGenerateStage);
      window.removeEventListener("wpa:questionnaire-save-stage", onSaveStage);
    };
  }, [aiBusyKey, saving, websiteId, selectedStage, responses]);

  useEffect(() => {
    const pending = location.state?.pendingWorkspaceAction;
    if (pending === "wpa:questionnaire-generate-stage") {
      openAiDialog({ type: "stage", mode: "generate" });
      navigate(location.pathname, { replace: true, state: {} });
    } else if (pending === "wpa:questionnaire-save-stage") {
      saveStage();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  if (!websiteId) {
    return (
      <main className="page-grid">
        <section className="card-modern">
          <h3>Website Stability Review Checklist</h3>
          <p>Select a website from the top switcher to start stage-wise data capture.</p>
        </section>
      </main>
    );
  }

  if (metaLoading || !assessmentStages.length || !stage?.key) {
    return (
      <main className="page-grid">
        <section className="card-modern">
          <h3>Website Stability Review Checklist</h3>
          <p>{metaError || "Loading assessment template from database..."}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="page-grid question-layout">
      <section className="card-modern stage-list">
        <h3>Website Stability Review Checklist</h3>
        <p className="muted">Use this in meetings to capture structured evidence before recommendation.</p>
        {assessmentStages.map((entry) => {
          const progress = savedStages.find((row) => row.stage_key === entry.key)?.completion_percent || 0;
          return (
            <button
              key={entry.key}
              className={`stage-item ${selectedStage === entry.key ? "active" : ""}`}
              onClick={() => setSelectedStage(entry.key)}
            >
              <span>{entry.label}</span>
              <strong>{progress}%</strong>
            </button>
          );
        })}
      </section>

      <section className="card-modern stage-form">
        <div className="guidance-panel">
          <h4>Professional Positioning</h4>
          <ul>
            {openingGuidance.map((line) => <li key={line}>{line}</li>)}
          </ul>
          <h4>Main Objectives</h4>
          <ul>
            {mainObjectives.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </div>

        <div className="stage-head">
          <div>
            <h3>{stage.label}</h3>
            <p className="muted">{stage.description}</p>
            {aiAssistMessage ? <p className="error">{aiAssistMessage}</p> : null}
          </div>
          <span className="progress-pill">{completion}% complete</span>
        </div>

        {stage.questions.map((question, index) => {
          const key = `q${index + 1}`;
          const selectedIncidentIds = answerIncidentMap[key] || [];
          const attachments = attachmentsMap[key] || [];
          return (
            <article key={key} className="prompt-block">
              <p>{index + 1}. {question}</p>
              <SimpleTextEditor
                rows={6}
                value={responses[key] || ""}
                onChange={(nextValue) => setResponses((prev) => ({ ...prev, [key]: nextValue }))}
                placeholder="Write detailed notes, facts, evidence, and stakeholder input..."
                ariaLabel={`${stage.key}-${key}-editor`}
              />
              <AiFieldActions
                helper="Use AI to draft or refine this answer"
                loadingMode={aiBusyKey === `${key}:generate` ? "generate" : aiBusyKey === `${key}:rewrite` ? "rewrite" : ""}
                onGenerate={() => openAiDialog({ type: "question", questionKey: key, mode: "generate", questionText: question })}
                onRewrite={() => openAiDialog({ type: "question", questionKey: key, mode: "rewrite", questionText: question })}
              />

              <div className="answer-evidence-toolbar">
                <button type="button" className="icon-tool-btn" title="Link incidents" onClick={() => toggleIncidentPicker(key)}>
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3.9 12a5 5 0 0 1 5-5h3v2h-3a3 3 0 1 0 0 6h3v2h-3a5 5 0 0 1-5-5zm6.1 1h4v-2h-4v2zm5-6h3a5 5 0 1 1 0 10h-3v-2h3a3 3 0 0 0 0-6h-3V7z"/></svg>
                  <span>{selectedIncidentIds.length}</span>
                </button>
                <button type="button" className="icon-tool-btn" title="Attach files" onClick={() => toggleAttachmentPicker(key)}>
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M16.5 6.5 9 14a2.1 2.1 0 1 0 3 3l7.6-7.6a4 4 0 1 0-5.7-5.7L5.8 11.8a6 6 0 0 0 8.5 8.5l6.4-6.4-1.4-1.4-6.4 6.4a4 4 0 0 1-5.7-5.7l8.1-8.1a2 2 0 1 1 2.8 2.8z"/></svg>
                  <span>{attachments.length}</span>
                </button>
                <span className="muted evidence-mini-note">Evidence</span>
              </div>

              {activeIncidentPickerKey === key ? (
                <div className="answer-popup">
                  <div className="answer-popup-head">
                    <strong>Link Incidents</strong>
                    <button type="button" className="popup-close-btn" onClick={() => setActiveIncidentPickerKey("")} aria-label="Close incident picker">x</button>
                  </div>
                  <div className="answer-popup-body">
                    <div className="incident-picker-list">
                      {incidents.map((incident) => {
                        const isChecked = selectedIncidentIds.includes(Number(incident.id));
                        return (
                          <label key={incident.id} className="incident-picker-item">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={async (event) => {
                                const next = event.target.checked
                                  ? [...selectedIncidentIds, Number(incident.id)]
                                  : selectedIncidentIds.filter((id) => id !== Number(incident.id));
                                onIncidentSelectionChange(key, next);
                                await saveLinkedIncidents(key, next);
                              }}
                            />
                            <span>{incident.title}</span>
                            <small>{incident.severity} | {incident.status}</small>
                          </label>
                        );
                      })}
                      {!incidents.length ? <p className="muted">No incidents available yet.</p> : null}
                    </div>
                    {linkSavingKey === key ? <p className="muted compact-status">Saving incident links...</p> : null}
                  </div>
                </div>
              ) : null}

              {activeAttachmentPickerKey === key ? (
                <div className="answer-popup">
                  <div className="answer-popup-head">
                    <strong>Attachments</strong>
                    <button type="button" className="popup-close-btn" onClick={() => setActiveAttachmentPickerKey("")} aria-label="Close attachments">x</button>
                  </div>
                  <div className="answer-popup-body">
                    <input
                      ref={(el) => { fileInputRefs.current[key] = el; }}
                      type="file"
                      className="hidden-file-input"
                      onChange={(event) => onUploadAttachment(key, event.target.files?.[0])}
                    />
                    <button
                      type="button"
                      className="button-link ghost compact-popup-btn"
                      onClick={() => fileInputRefs.current[key]?.click()}
                    >
                      Browse Files
                    </button>
                    {uploadingKey === key ? <p className="muted">Uploading...</p> : null}
                    <div className="attachment-list compact">
                      {attachments.map((attachment) => (
                        <div key={attachment.id} className="attachment-item">
                          <a href={resolveAttachmentUrl(attachment.public_url)} target="_blank" rel="noreferrer">
                            {attachment.original_name}
                          </a>
                          <button type="button" className="button-link ghost" onClick={() => deleteAttachment(attachment.id)}>
                            Remove
                          </button>
                        </div>
                      ))}
                      {!attachments.length ? <p className="muted">No files attached yet.</p> : null}
                    </div>
                  </div>
                </div>
              ) : null}
            </article>
          );
        })}

        <button onClick={saveStage} disabled={saving}>{saving ? "Saving..." : "Save Stage Responses"}</button>
      </section>
      <AiAssistOptionsModal
        open={Boolean(aiDialog)}
        mode={aiDialog?.mode || "generate"}
        onClose={() => setAiDialog(null)}
        onSubmit={applyAiDialog}
      />
    </main>
  );
}
